import React, { useRef, useEffect, useState } from "react";
import { PropertyPalUserMessage } from "./pp-user-message";
import { PropertyPalBotMessage } from "./pp-bot-message";
import { PropertyPalScrollBottom } from "./pp-scroll-bottom";
import PropertyPalThinking from "./pp-thinking";
import { PropertyPalFeedbackButtons, type Feedback } from "./pp-feedback-buttons";
import type { ChatMessage, FeedbackStatus } from "./pp-layout";

interface ChatBodyProps {
  messages: ChatMessage[];
  chatbotThinking: boolean;
  feedbackType?: Feedback;
  feedbackStatus?: FeedbackStatus;
  onFeedbackChange?: (feedback: Feedback) => void;
}

const componentContainer = "relative flex flex-col self-stretch bg-neutral-white first:mt-auto flex-1 min-h-0";
const scrollableContainer = "flex flex-col gap-4 items-center w-full overflow-y-auto h-full p-4";

export const PropertyPalChatBody: React.FC<ChatBodyProps> = ({
  messages,
  chatbotThinking,
  feedbackType,
  feedbackStatus,
  onFeedbackChange,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [showScrollBottom, setShowScrollBottom] = useState(false);

  // Extracted scroll-to-bottom logic
  const scrollToBottom = () => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages, feedbackStatus]);

  // Show button if not at bottom
  const handleScroll = () => {
    const el = containerRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 10;
    setShowScrollBottom(!atBottom);
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener("scroll", handleScroll, { passive: true });
    // Check on mount
    handleScroll();
    return () => {
      el.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Find index of the last bot message
  const lastBotIndex = messages.reduce((acc, msg, i) => (msg.type === "bot" ? i : acc), -1);

  return (
    <div className={componentContainer} role="log" aria-live="polite" aria-label="Chat messages" tabIndex={0}>
      <div className={scrollableContainer} ref={containerRef}>
        {messages.map((msg, index) => (
          <React.Fragment key={msg.id}>
            {msg.type === "user" ? (
              <PropertyPalUserMessage message={msg.text} />
            ) : (
              <PropertyPalBotMessage message={msg.text} />
            )}
            {index === lastBotIndex && index !== 0 && !chatbotThinking && (
              <div className="flex flex-col gap-2 items-end self-stretch pr-[18px]">
                <PropertyPalFeedbackButtons feedback={feedbackType} onFeedbackChange={onFeedbackChange} />
                {feedbackStatus === "success" && (
                  <p className="text-body-s font-normal leading-[1.5] text-light-slate px-3 w-full text-left">
                    Thanks for your feedback! Your input helps us make My Property Pal even smarter for you and others.
                  </p>
                )}
                {feedbackStatus === "error" && (
                  <p className="text-body-s font-normal leading-[1.5] text-brick-300 px-3 w-full text-left">
                    Sorry, there was a problem submitting your feedback. Please try again.
                  </p>
                )}
              </div>
            )}
          </React.Fragment>
        ))}
        {chatbotThinking && <PropertyPalThinking />}
      </div>
      <PropertyPalScrollBottom isVisible={showScrollBottom} onClick={scrollToBottom} />
    </div>
  );
};
